/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { isSameDay } from '@features/agenda/utils/agenda-dates';
import { eventColor } from './event-color';
import { DAY_START_HOUR, MIN_CHIP_HEIGHT, eventPlacement, nowOffset, splitAllDay,
  timeRange,
} from './time-grid';
import { computeEventLayouts } from './event-layout';
import { Text, UnstyledButton } from '@/shared/ui';

export const SLOT_HEIGHT = 56;
export const HOURS = Array.from({ length: 24 }, (_, i) => i + DAY_START_HOUR);

/**
 * Reports whether the given date is today.
 * @param day - The date to test.
 * @returns True when `day` falls on the current calendar day.
 */
export const isToday = (day: Date): boolean => dayjs().isSame(dayjs(day), 'day');

interface WeekColumnProps {
  day: Date;
  events: ExpandedEvent[];
  calendars: Calendar[];
  onSelect: (event: ExpandedEvent) => void;
  onSlotClick?: (date: Date) => void;
}

/**
 * Typography for event chips, applied inline.
 *
 * The shared `Text` component ships its own `whitespace-pre-wrap` and
 * font-weight classes, which beat the `truncate` utility's `nowrap` and
 * `font-bold` at equal specificity — text wrapped onto extra lines, clipping
 * the time off short chips, and the bold never landed. An inline style is the
 * one thing that outranks both.
 */
const NOWRAP = { whiteSpace: 'nowrap', fontWeight: 700 } as const;

/** Single day column inside the week grid, with its events positioned. */
export const WeekColumn: React.FC<WeekColumnProps> = ({ day, events, calendars, onSelect, onSlotClick }) => {
  const { t } = useTranslation('features/agenda');
  // All-day events live in the band above the grid, not pinned to 00:00 here.
  const colEvents = splitAllDay(
    events.filter((e) => isSameDay(e.start_at_utc, day))
  ).timed;
  const today = isToday(day);
  const now = today ? nowOffset(day, SLOT_HEIGHT) : null;
  const layouts = computeEventLayouts(colEvents);
  const GUTTER = 2;
  return (
    <div
      data-testid={today ? 'agenda-week-col-today' : undefined}
      className="relative border-l border-r border-[var(--border-ultra-subtle)]"
      style={today ? { background: 'color-mix(in srgb, var(--seed-base) 4%, transparent)' } : undefined}
    >
      {HOURS.map((h) => (
        <UnstyledButton
          key={h}
          type="button"
          aria-label={`Criar evento às ${String(h).padStart(2, '0')}:00`}
          onClick={() => {
            if (!onSlotClick) return;
            const d = new Date(day);
            d.setHours(h, 0, 0, 0);
            onSlotClick(d);
          }}
          className="block w-full cursor-pointer border-b border-[var(--border-ultra-subtle)] transition-colors hover:bg-[var(--seed-base)]/[0.04] focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]/40"
          style={{ height: SLOT_HEIGHT }}
        >
          {null}
        </UnstyledButton>
      ))}
      {now !== null && (
        <div
          data-testid="agenda-now-line-week"
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 z-10"
          style={{ top: now, height: 'var(--space-2)', marginTop: 'calc(-1 * var(--space-1))' }}
        >
          <Text component="span" className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--status-error)]" />
          <Text component="span" className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[var(--status-error)]" />
        </div>
      )}
      {colEvents.map((ev) => {
        const color = eventColor(ev, calendars);
        const { top, height } = eventPlacement(ev, SLOT_HEIGHT, MIN_CHIP_HEIGHT);
        const { leftPct, widthPct, colIndex } = layouts.get(
          `${ev.event_id}:${ev.occurrence_start}`
        ) ?? { leftPct: 0, widthPct: 100, colIndex: 0 };
        const isRecurring = Array.isArray(ev.recurrence) && ev.recurrence.length > 0;
        return (
          <UnstyledButton
            key={`${ev.event_id}-${ev.occurrence_start}`}
            type="button"
            data-testid="agenda-event-chip"
            title={`${timeRange(ev)} · ${ev.title}`}
            onClick={() => onSelect(ev)}
            className="absolute min-w-0 overflow-hidden rounded-[var(--radius-sm)] border-l-[3px] px-(--space-1-5) py-(--space-0-5) text-left flex flex-col justify-start transition-shadow hover:shadow-[var(--shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]"
            style={{
              top,
              height,
              left: `calc(${leftPct}% + ${GUTTER}px)`,
              width: `calc(${widthPct}% - ${GUTTER * 2}px)`,
              zIndex: colIndex + 1,
              background: `color-mix(in srgb, ${color} 12%, var(--surface-primary))`,
              borderColor: color,
            }}
          >
            {/* Title over time, one line each. The chip is never shorter than
                MIN_CHIP_HEIGHT, so two lines always fit and neither has to share a
                row with the other — which is what used to let a long title eat the
                time in the ellipsis. */}
            <Text component="span"
              style={{ color, ...NOWRAP }}
              className={`block w-full truncate text-xs leading-tight ${isRecurring ? 'pr-(--space-3-5)' : ''}`}
            >
              {ev.title}
            </Text>
            <Text component="span"
              style={{ color, ...NOWRAP }}
              className="block w-full truncate text-xs leading-tight"
            >
              {timeRange(ev)}
            </Text>
            {isRecurring && (
              <Text component="span"
                data-testid="recurrence-indicator"
                aria-label={t('preview.recurring')}
                className="absolute top-0.5 right-0.5 flex items-center justify-center"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 15 15"
                  fill="none"
                  aria-hidden="true"
                  className="opacity-70"
                  style={{ color }}
                >
                  <path
                    d="M3.5 2a.5.5 0 0 1 .5.5v1.77A5.5 5.5 0 0 1 13 7.5a.5.5 0 0 1-1 0A4.5 4.5 0 0 0 4.58 5H6.5a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5v-3a.5.5 0 0 1 .5-.5ZM2 7.5a.5.5 0 0 1 1 0A4.5 4.5 0 0 0 10.42 10H8.5a.5.5 0 0 1 0-1h3a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0V10.73A5.5 5.5 0 0 1 2 7.5Z"
                    fill="currentColor"
                  />
                </svg>
              </Text>
            )}
          </UnstyledButton>
        );
      })}
    </div>
  );
};
